<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h4 mb-0">Retours fournisseur ({{ totalRows }})</h1>

        <div class="d-flex gap-2">
          <ModuleActivityButton :module="['supplier_returns', 'SupplierReturn', 'SupplierReturnItem']" label="Retours fournisseur" />

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            :title="filtersVisible ? 'Masquer les filtres' : 'Afficher les filtres'"
            @click="toggleFilters"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>

          <button class="btn btn-primary btn-sm btn-icon-label" type="button" @click="$router.push({ name: 'SupplierReturnCreate' })">
            <i class="fa fa-plus"></i>
            <span class="btn-label ms-1">Nouveau retour</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success mb-3 py-2">
        {{ successMessage }}
      </div>

      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <div class="row g-2 align-items-end">
            <div class="col-md-3">
              <label class="form-label small mb-1">Du</label>
              <input type="date" class="form-control form-control-sm" v-model="filters.date_from" />
            </div>

            <div class="col-md-3">
              <label class="form-label small mb-1">Au</label>
              <input type="date" class="form-control form-control-sm" v-model="filters.date_to" />
            </div>

            <div class="col-md-3">
              <label class="form-label small mb-1">Fournisseur</label>
              <select class="form-select form-select-sm" v-model="filters.supplier_id">
                <option value="">Tous</option>
                <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">
                  {{ supplier.name }}
                </option>
              </select>
            </div>

            <div class="col-md-3">
              <label class="form-label small mb-1">Statut</label>
              <select class="form-select form-select-sm" v-model="filters.status">
                <option value="">Tous</option>
                <option value="draft">Brouillon</option>
                <option value="validated">Validé</option>
              </select>
            </div>

            <div class="col-12 module-filter-actions mt-2">
              <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetFilters">
                <i class="fa fa-rotate-left"></i>
                <span class="btn-label ms-1">Réinitialiser</span>
              </button>
              <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="fetchSupplierReturns">
                <i class="fa fa-search"></i>
                <span class="btn-label ms-1">Filtrer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <BootstrapDataTable
        :response="supplierReturnsResponse"
        :columns="tableColumns"
        :loading="isLoading"
        :selectable="canBulkDeleteSupplierReturns"
        v-model:selected-row-ids="bulkSelectedIds"
        :is-row-selectable="canSelectSupplierReturnForBulkDelete"
        :selection-disabled="bulkActionPending"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :per-page="table.perPage"
        :active-filter-count="activeFilterCount"
        state-key="supplier-returns.list"
        searchable
        v-model:search-value="filters.search"
        search-placeholder="Rechercher référence ou fournisseur..."
        detail-route-name="SupplierReturnShow"
        empty-text="Aucun retour fournisseur trouvé."
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
        @state-restored="Object.assign(table, { page: $event.page, perPage: $event.perPage })"
        @refresh="fetchSupplierReturns"
      >
        <template #bulk-actions="{ selectedCount, isOverLimit }">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            :disabled="bulkActionPending || isOverLimit"
            @click="bulkDeleteSupplierReturns"
          >
            <i class="fa fa-trash me-1" aria-hidden="true"></i>Supprimer {{ selectedCount }}
          </button>
        </template>

        <template #cell-status="{ row }">
          <span class="badge" :class="statusClass(row.status)">
            {{ statusLabel(row.status) }}
          </span>
        </template>

        <template #[`cell-purchase.reference`]="{ row }">
          {{ row.purchase?.reference || '-' }}
        </template>

        <template #cell-total_amount="{ row }">
          {{ $format.currency(row.total_amount || 0) }}
        </template>

        <template #cell-return_date="{ row }">
          {{ $format.date(row.return_date) }}
        </template>
      </BootstrapDataTable>

    </template>
  </AdminLayout>
</template>

<script>
import {
  cleanFilterParams,
  countActiveFilters,
  extractApiTotal,
  extractApiList,
  loadModuleFilterState,
  saveModuleFilterValues,
  saveModuleFilterVisibility,
} from '@/support/moduleFilters';
import { bulkActionViewMixin } from '@/support/bulkActions';
import { hasPermission } from '@/support/permissions';

const FILTER_MODULE = 'supplier_returns';
const DEFAULT_FILTERS = {
  search: '',
  date_from: '',
  date_to: '',
  supplier_id: '',
  status: '',
};

export default {
  name: 'SupplierReturnsView',
  mixins: [bulkActionViewMixin],
  data() {
    return {
      supplierReturns: [],
      supplierReturnsResponse: null,
      suppliers: [],
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      filters: { ...DEFAULT_FILTERS },
      filtersVisible: false,
      table: {
        page: 1,
        perPage: 10,
        sortBy: 'return_date',
        sortDir: 'desc',
      },
      tableColumns: [
        { key: 'reference', label: 'Référence' },
        { key: 'purchase.reference', label: 'Commande' },
        { key: 'supplier_name', label: 'Fournisseur' },
        { key: 'status', label: 'Statut', sortable: true, class: 'text-center' },
        { key: 'total_amount', label: 'Montant', sortable: true, class: 'text-end' },
        { key: 'return_date', label: 'Date', sortable: true },
      ],
    };
  },
  mounted() {
    this.restoreFilters();

    if (this.$route.query.purchase_id) {
      this.$router.replace({
        name: 'SupplierReturnCreate',
        query: { purchase_id: this.$route.query.purchase_id },
      });
      return;
    }

    this.fetchSuppliers();
    this.fetchSupplierReturns();
  },
  computed: {
    currentUser() {
      return this.$store?.getters?.['auth/currentUser']
        || this.$store?.getters?.['auth/user']
        || this.$store?.state?.auth?.user
        || null;
    },
    canBulkDeleteSupplierReturns() {
      return hasPermission(this.currentUser, 'supplier_returns.delete');
    },
    totalRows() {
      return extractApiTotal(this.supplierReturnsResponse, this.supplierReturns.length);
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, DEFAULT_FILTERS, ['search']);
    },
  },
  watch: {
    filters: {
      deep: true,
      handler(filters) {
        saveModuleFilterValues(FILTER_MODULE, filters);
      },
    },
    filtersVisible(value) {
      saveModuleFilterVisibility(FILTER_MODULE, value);
    },
  },
  methods: {
    canSelectSupplierReturnForBulkDelete(supplierReturn) {
      return supplierReturn?.status === 'draft';
    },

    async bulkDeleteSupplierReturns() {
      await this.performBulkAction({
        url: '/supplier-returns/bulk',
        method: 'delete',
        itemLabel: 'retour fournisseur',
        confirmation: (count) => ({
          header: 'Supprimer les retours fournisseur',
          message: `Supprimer les ${count} retours fournisseur brouillon sélectionnés ?`,
          acceptLabel: `Supprimer ${count}`,
          acceptClass: 'p-button-danger',
        }),
        refresh: () => this.fetchSupplierReturns(),
      });
    },

    restoreFilters() {
      const state = loadModuleFilterState(FILTER_MODULE, DEFAULT_FILTERS);
      this.filters = state.values;
      this.filtersVisible = state.visible;
    },

    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },

    async fetchSuppliers() {
      try {
        const res = await this.$api.get('/suppliers', { params: { limit: 1000 } });
        this.suppliers = res.data.data || res.data || [];
      } catch (error) {
        this.suppliers = [];
      }
    },

    buildParamsFromFilters() {
      return {
        ...cleanFilterParams(this.filters),
        paginate: 1,
        page: this.table.page,
        per_page: this.table.perPage,
        sort_by: this.table.sortBy,
        sort_dir: this.table.sortDir,
      };
    },

    async fetchSupplierReturns() {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const res = await this.$api.get('/supplier-returns', {
          params: this.buildParamsFromFilters(),
        });
        this.supplierReturnsResponse = res.data;
        this.supplierReturns = extractApiList(res.data);
      } catch (error) {
        this.errorMessage = 'Impossible de charger les retours fournisseur.';
      } finally {
        this.isLoading = false;
      }
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.table.page = 1;
      this.fetchSupplierReturns();
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
      this.table.page = 1;
      this.fetchSupplierReturns();
    },

    handlePageChange(page) {
      this.table.page = page;
      this.fetchSupplierReturns();
    },

    handlePerPageChange(perPage) {
      this.table.perPage = perPage;
      this.table.page = 1;
      this.fetchSupplierReturns();
    },

    handleTableSearch(search) {
      this.filters.search = search;
      this.table.page = 1;
      this.fetchSupplierReturns();
    },

    statusLabel(status) {
      return status === 'validated' ? 'Validé' : 'Brouillon';
    },

    statusClass(status) {
      return status === 'validated' ? 'bg-success' : 'bg-secondary';
    },

  },
};
</script>
